import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLazyGetFundingListQuery, useLazyGetOrganizationForFundingQuery } from '@/store/api';
import { 
  IFundingListResponse, 
  IFundingTableRow,
  IFundingFilterModel,
  IFundingSortModel,
  IFundingPaginationModel,
  Funding,
  IPropertyDetail,
  FundingKeys,
  IOrganizationWithPropertiesResponse,
  IPropertyWithMerchants,
  IMerchantAccountInfo,
  IProcessedFunding,
  BulkReprocessHandler
} from '@/types/admin/funding';
import { useAdminUser } from './useAdminUser';
import { handleApiError, EnhancedError } from '@/utils/admin/errorHandling';
import { ROWS_PER_PAGE } from '@/constants/admin';
import { processedFundingToTableRow } from '@/utils/admin/fundingUtils';
import { useDataTableState } from '@/contexts/admin/DataTableContext';

/**
 * Normalize table row data (matching actual API response structure)
 */
const normalizeTableRow = (row: Funding): IFundingTableRow => {
  return {
    id: row.id,
    property: row.property,
    merchantId: row.merchantId,
    fundingDate: new Date(row.fundingDate), // API uses fundingDate, not date
    lastStatus: row.lastStatus,
    funding_amount: row.fundingJournalData,
    fee_amount: row.fundingJournalData,
    net_amount: row.fundingJournalData,
    actions: row.id,
  };
};

// --- Split hooks: state, data, actions ---

const useFundingTableState = () => {
  const table = useDataTableState('admin:funding');
  const [selectedRows, setSelectedRows] = useState<IFundingTableRow[]>([]);
  const adminUser = useAdminUser();
  
  // Track organization changes and clear filters when org changes
  const prevOrgId = useRef<number | undefined>(adminUser.selectedOrganizationId);
  
  useEffect(() => {
    // Only reset if organization actually changed (skip initial mount)
    if (prevOrgId.current !== undefined && 
        prevOrgId.current !== adminUser.selectedOrganizationId) {
      
      // Clear filters (removes property/merchantId selections from old org)
      table.setFilter({ items: [] });
      
      // Reset pagination to first page
      table.setPagination({ page: 0, pageSize: table.paginationModel.pageSize });
      
      // Clear selected rows
      setSelectedRows([]);
    }
    
    // Update ref for next change detection
    prevOrgId.current = adminUser.selectedOrganizationId;
  }, [adminUser.selectedOrganizationId, table]);
  
  return {
    filterModel: table.filterModel as IFundingFilterModel,
    setFilterModel: (m: IFundingFilterModel) => table.setFilter(m as any),
    sortModel: table.sortModel as IFundingSortModel[],
    setSortModel: (s: IFundingSortModel[]) => table.setSort(s as any),
    paginationModel: table.paginationModel as IFundingPaginationModel,
    setPaginationModel: (p: IFundingPaginationModel) => table.setPagination(p as any),
    searchQuery: table.searchQuery,
    setSearchQuery: (q: string) => table.setSearch(q),
    selectedRows, setSelectedRows,
  };
};

const useFundingData = (
  selectedOrganizationId: number | undefined,
  filterModel: IFundingFilterModel,
  sortModel: IFundingSortModel[],
  paginationModel: IFundingPaginationModel,
) => {
  const [enhancedError, setEnhancedError] = useState<EnhancedError | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const retryCountRef = useRef<number>(0);
  const [propertyData, setPropertyData] = useState<IPropertyDetail[]>();
  const [tableRows, setTableRows] = useState<IFundingTableRow[]>([]);

  const [getFundingList, { data, isLoading, isUninitialized }] = useLazyGetFundingListQuery();
  const [getOrganization] = useLazyGetOrganizationForFundingQuery();

  /**
   * Reset property data when organization changes
   */
  useEffect(() => {
    setPropertyData(undefined);
  }, [selectedOrganizationId]);

  /**
   * Fetch property data for filters (matching admin-web logic)
   */
  const fetchPropertyData = useCallback(async () => {
    if (!selectedOrganizationId || propertyData) return;

    try {
      const listResponse: IOrganizationWithPropertiesResponse = await getOrganization({
        organizationId: selectedOrganizationId,
        includePropertyDetails: true,
      }).unwrap();

      if (listResponse?.response?.properties) {
        const propertyDetails: IPropertyDetail[] = listResponse.response.properties.map(
          (property: IPropertyWithMerchants) => {
            const merchantIds: string[] = property.merchantAccounts?.map(
              (merchant: IMerchantAccountInfo) => merchant.merchantId,
            ) || [];
            return {
              id: property.id,
              name: property.marketingName,
              merchantIds: merchantIds,
            };
          },
        );
        setPropertyData(propertyDetails);
      }
    } catch (fetchError) {
      console.error('Failed to fetch property data:', fetchError);
    }
  }, [selectedOrganizationId, propertyData, getOrganization]);

  /**
   * Enhanced trigger fetch with error handling
   */
  const triggerFetch = useCallback(async () => {
    if (!selectedOrganizationId) {
      console.warn('No organization selected, cannot fetch funding list');
      return;
    }

    // Clear previous error
    setEnhancedError(null);

    try {
      const result = await getFundingList({
        sortModel,
        filterModel,
        paginationModel,
        selectedOrganizationId: selectedOrganizationId!,
      }).unwrap();
      
      // Reset retry count on success
      retryCountRef.current = 0;
      setRetryCount(0);
    } catch (fetchError) {
      // Handle and log the error
      const errorDetails = handleApiError(fetchError, '/api/v1/funding/list', retryCountRef.current);
      setEnhancedError(errorDetails);
      setRetryCount(prev => {
        const next = prev + 1;
        retryCountRef.current = next;
        return next;
      });
      
      console.error('🔴 Failed to fetch funding list after retries:', errorDetails);
    }
  }, [
    selectedOrganizationId,
    sortModel,
    filterModel,
    paginationModel,
    getFundingList,
  ]);

  /**
   * Safe data access with default values
   */
  const fundingListData: IFundingListResponse = useMemo(() => {
    if (!data || !data.response || !data.response.count || !data.response.rows) {
      return { count: 0, rows: [] };
    }
    return { count: data.response.count, rows: data.response.rows };
  }, [data]);

  /**
   * Normalized table rows for display
   */
  useEffect(() => {
    const nextRows = fundingListData?.rows?.map(normalizeTableRow) || [];
    setTableRows(nextRows);
  }, [fundingListData]);

  /**
   * Loading state computation - only show loading on initial load or when no data exists
   */
  const isTableLoading = (isLoading || isUninitialized) && (fundingListData.rows.length === 0);

  /**
   * Fetch property data when organization changes
   */
  useEffect(() => {
    if (selectedOrganizationId) {
      fetchPropertyData();
    }
  }, [selectedOrganizationId, fetchPropertyData]);

  /**
   * Kick off the initial fetch once we have an organization and the query is uninitialized.
   * This prevents the app from staying in a perpetual loading state while also avoiding double fetches.
   */
  useEffect(() => {
    if (selectedOrganizationId && isUninitialized) {
      triggerFetch();
    }
  }, [selectedOrganizationId, isUninitialized, triggerFetch]);

  /**
   * When filters or sort change, reset to first page.
   * The pagination effect below will perform the fetch to avoid double requests.
   */
  // (Fetch is intentionally NOT triggered here.)

  /**
   * Fetch data when pagination changes (immediate, no debounce needed)
   */
  useEffect(() => {
    if (selectedOrganizationId && !isUninitialized) {
      triggerFetch();
    }
    // Intentionally exclude triggerFetch from deps to avoid identity changes (e.g., retries) causing loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, selectedOrganizationId, isUninitialized]);

  /**
   * Reset pagination when filters or sort change (but don't trigger fetch here)
   */
  // Pagination reset on filter/sort changes is handled in parent hook

  /**
   * Update filter model
   */
  // State updates are handled in parent hook

  /**
   * Handle single funding reprocess completion
   */
  const handleReprocessComplete = useCallback((updatedFunding: IFundingTableRow) => {
    // In-place update of the affected row to avoid a full refetch
    setTableRows((prev) => {
      const updated = prev.map((row) => {
        if (row.id !== updatedFunding.id) return row;
        return {
          ...row,
          // Prefer values from updatedFunding when provided
          lastStatus: updatedFunding.lastStatus ?? row.lastStatus,
          fundingDate: updatedFunding.fundingDate ?? row.fundingDate,
          funding_amount: updatedFunding.funding_amount ?? row.funding_amount,
          fee_amount: updatedFunding.fee_amount ?? row.fee_amount,
          net_amount: updatedFunding.net_amount ?? row.net_amount,
        } as IFundingTableRow;
      });
      return updated;
    });

    // If selected, update selection model too (no-op here; parent maintains selection state)
  }, []);

  /**
   * Handle bulk funding reprocess completion
   */
  const handleBulkReprocessComplete: BulkReprocessHandler = useCallback((updatedFundings: IProcessedFunding[]) => {
    // Convert processed fundings to table rows and apply in-place updates
    const updatedRows = updatedFundings.map(processedFundingToTableRow);
    setTableRows((prev) => {
      const byId = new Map<number, IFundingTableRow>(prev.map((r) => [r.id, r]));
      for (const u of updatedRows) {
        const current = byId.get(u.id);
        if (current) {
          byId.set(u.id, {
            ...current,
            lastStatus: u.lastStatus ?? current.lastStatus,
            fundingDate: u.fundingDate ?? current.fundingDate,
            funding_amount: u.funding_amount ?? current.funding_amount,
            fee_amount: u.fee_amount ?? current.fee_amount,
            net_amount: u.net_amount ?? current.net_amount,
          });
        }
      }
      return Array.from(byId.values());
    });

  }, []);

  /**
   * Handle download completion
   */
  const handleDownloadComplete = useCallback(() => {
    // no-op here; parent can clear selection
  }, []);

  /**
   * Select all visible rows
   */
  // selection helpers are managed in parent hook

  /**
   * Refresh data manually
   */
  const refresh = useCallback(() => {
    if (selectedOrganizationId) {
      triggerFetch();
    }
  }, [selectedOrganizationId, triggerFetch]);

  /**
   * Go to next page
   */
  /**
   * Manual retry function for user-initiated retries
   */
  const manualRetry = useCallback(() => {
    setEnhancedError(null);
    triggerFetch();
  }, [triggerFetch]);

  return {
    fundingListData,
    tableRows,
    propertyData,
    isTableLoading,
    enhancedError,
    retryCount,
    canRetry: enhancedError?.shouldRetry || false,
    manualRetry,
    refresh,
    triggerFetch,
    handleReprocessComplete,
    handleBulkReprocessComplete,
    handleDownloadComplete,
  };
};

export const useFunding = () => {
  const adminUser = useAdminUser();
  const state = useFundingTableState();
  const dataApi = useFundingData(
    adminUser.selectedOrganizationId,
    state.filterModel,
    state.sortModel,
    state.paginationModel,
  );

  // When filters, sort, or search change: reset to first page and fetch
  // This mirrors admin-web behavior where changing filters resets pagination
  // and triggers a server-side refresh.
  useEffect(() => {
    // If we're not on the first page, reset to page 0 and let the pagination effect fetch
    if (state.paginationModel.page !== 0) {
      state.setPaginationModel({ ...state.paginationModel, page: 0 });
      return;
    }
    // Already on page 0 → trigger fetch immediately
    dataApi.triggerFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filterModel, state.sortModel, state.searchQuery]);

  return {
    // Data
    data: dataApi.fundingListData,
    tableRows: dataApi.tableRows,
    propertyData: dataApi.propertyData,
    
    // State
    isLoading: dataApi.isTableLoading,
    error: null,
    
    enhancedError: dataApi.enhancedError,
    retryCount: dataApi.retryCount,
    canRetry: dataApi.canRetry,
    manualRetry: dataApi.manualRetry,
    
    // Pagination/state
    paginationModel: state.paginationModel,
    updatePaginationModel: (m: IFundingPaginationModel) => state.setPaginationModel(m),
    goToNextPage: () => {
      const maxPage = Math.ceil(dataApi.fundingListData.count / state.paginationModel.pageSize) - 1;
      if (state.paginationModel.page < maxPage) {
        state.setPaginationModel({ ...state.paginationModel, page: state.paginationModel.page + 1 });
      }
    },
    goToPreviousPage: () => {
      if (state.paginationModel.page > 0) {
        state.setPaginationModel({ ...state.paginationModel, page: state.paginationModel.page - 1 });
      }
    },
    hasNextPage: useMemo(() => {
      const maxPage = Math.ceil(dataApi.fundingListData.count / state.paginationModel.pageSize) - 1;
      return state.paginationModel.page < maxPage;
    }, [dataApi.fundingListData.count, state.paginationModel]),
    hasPreviousPage: useMemo(() => state.paginationModel.page > 0, [state.paginationModel.page]),
    
    // Filtering & Sorting
    filterModel: state.filterModel,
    updateFilterModel: (m: IFundingFilterModel) => state.setFilterModel(m),
    sortModel: state.sortModel,
    updateSortModel: (m: IFundingSortModel[]) => state.setSortModel(m),
    searchQuery: state.searchQuery,
    updateSearchQuery: (q: string) => state.setSearchQuery(q),
    
    // Row Selection
    selectedRows: state.selectedRows,
    updateRowSelection: (rows: IFundingTableRow[]) => state.setSelectedRows(rows),
    clearRowSelection: () => state.setSelectedRows([]),
    selectAllRows: () => state.setSelectedRows(dataApi.tableRows),
    deselectAllRows: () => state.setSelectedRows([]),
    
    // Action Handlers
    handleReprocessComplete: dataApi.handleReprocessComplete,
    handleBulkReprocessComplete: dataApi.handleBulkReprocessComplete,
    handleDownloadComplete: dataApi.handleDownloadComplete,
    
    // Actions
    refresh: dataApi.refresh,
    getFundingList: dataApi.triggerFetch,
  };
};
